import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Stethoscope } from "lucide-react";
import { CAPABILITIES, MEMBERSHIPS, PROFILE, STATS } from "../data/cv";
import { fadeUp, stagger, viewportOnce } from "../lib/anim";

const Counter = ({ target, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / 1500);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return (
    <p ref={ref} className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-grad" data-testid="stat-counter">
      {val}
      {suffix}
    </p>
  );
};

const CapColumn = ({ title, items }) => (
  <div>
    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-neon mb-4">{title}</p>
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gradient-to-r from-neon to-vio shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export default function Stats() {
  return (
    <section id="about" className="relative py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> 01 — Profile
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Clinical expertise, <span className="text-grad">backed by execution.</span>
          </motion.h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Narrative */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-5 panel spot-card rounded-2xl p-6 sm:p-8"
            data-testid="profile-narrative-card"
          >
            <p className="text-base sm:text-lg leading-relaxed text-slate-300">
              Dr. Rahul Kanungo is a <span className="text-white font-semibold">consultant pathologist</span> with a special interest in
              hematopathology practice — and a builder's range: <span className="text-grad font-semibold">four production
              health-tech products</span>, an MBA in healthcare management, NABL-grade quality leadership, research
              and teaching — plus a 17K+ organic audience for brand collaborations. Gold medal in MD Pathology.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-400">
              <MapPin size={14} className="text-neon" /> {PROFILE.location}
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
              <Stethoscope size={14} className="text-neon" /> Currently: Assistant Professor, Lab Hematology — IMS &amp; SUM Hospital-II
            </div>
            <div className="mt-7 flex flex-wrap gap-2">
              {MEMBERSHIPS.map((m) => (
                <span key={m} className="text-[11px] font-mono text-slate-400 border border-white/10 rounded-full px-3 py-1.5 bg-white/[0.03]">
                  {m}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Counters */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-6">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                custom={i + 1}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="panel spot-card rounded-2xl p-5 sm:p-6 flex flex-col justify-between"
                data-testid="stat-card"
              >
                <Counter target={s.value} suffix={s.suffix} />
                <div className="mt-4">
                  <p className="text-sm font-medium text-slate-200">{s.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.sub}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Photo tile */}
          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-3 frame-spot clip-notch p-[2px] bg-gradient-to-b from-neon/40 to-vio/30 rounded-none"
            data-testid="photo-tile-lab"
          >
            <div className="clip-notch overflow-hidden h-full min-h-[280px] bg-panel">
              <img src="/images/lab.jpg" alt="Dr. Rahul Kanungo at the digital pathology workstation, IMS & SUM Hospital" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </motion.div>
        </div>

        {/* Capability matrix */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-6 panel spot-card rounded-2xl p-6 sm:p-8"
          data-testid="capability-matrix"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <CapColumn title="Clinical bench" items={CAPABILITIES.clinical} />
            <CapColumn title="Digital & systems" items={CAPABILITIES.tech} />
          </div>
        </motion.div>
      </div>
    </section>
  );
}